import { urlFor } from '../client';

const DEFAULT_QUALITY = 80;

const getSourceDimensions = (source) => {
  const reference = source?.asset?._ref
    || source?.asset?._id
    || source?._ref
    || source?._id
    || '';
  const match = reference.match(/-(\d+)x(\d+)-[^-]+$/);

  if (!match) {
    return null;
  }

  return {
    width: Number(match[1]),
    height: Number(match[2]),
  };
};

const constrainCandidate = (candidate, sourceDimensions) => {
  if (!sourceDimensions) {
    return candidate;
  }

  const widthScale = sourceDimensions.width / candidate.width;
  const heightScale = candidate.height
    ? sourceDimensions.height / candidate.height
    : 1;
  const scale = Math.min(1, widthScale, heightScale);

  return {
    width: Math.round(candidate.width * scale),
    ...(candidate.height && { height: Math.round(candidate.height * scale) }),
  };
};

export const buildSanityImageUrl = (
  source,
  { width, height },
  { quality = DEFAULT_QUALITY, fit = 'crop' } = {},
) => {
  if (!source || !width) {
    return '';
  }

  let builder = urlFor(source)
    .width(width)
    .quality(quality)
    .auto('format');

  if (height) {
    builder = builder.height(height).fit(fit);
  }

  return builder.url();
};

export const getSanityImageProps = (
  source,
  {
    candidates,
    sizes,
    quality = DEFAULT_QUALITY,
    fit = 'crop',
  },
) => {
  const sourceDimensions = getSourceDimensions(source);
  const uniqueCandidates = new Map();

  candidates
    .map((candidate) => constrainCandidate(candidate, sourceDimensions))
    .filter(({ width }) => Number.isFinite(width) && width > 0)
    .forEach((candidate) => uniqueCandidates.set(candidate.width, candidate));

  const validCandidates = [...uniqueCandidates.values()]
    .sort((left, right) => left.width - right.width);

  if (!source || validCandidates.length === 0) {
    return {};
  }

  const urls = validCandidates.map((candidate) => ({
    ...candidate,
    url: buildSanityImageUrl(source, candidate, { quality, fit }),
  }));

  return {
    src: urls[urls.length - 1].url,
    srcSet: urls.map(({ url, width }) => `${url} ${width}w`).join(', '),
    sizes,
  };
};
